import { AuthError, isEmailNotVerified, login } from '@/lib/auth-service';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';

export interface LoginCardProps {
  /** Called once authentication succeeds (after the brief success animation). */
  onSuccess: () => void;
  /** Shown in the form-level alert on mount (e.g. a session-expired notice); cleared on first edit. */
  initialMessage?: string;
}

type FieldErrors = {
  email?: string;
  password?: string;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const COPY = {
  emailEmpty: 'Saisissez votre adresse e-mail.',
  emailInvalid: "Cette adresse e-mail n'est pas valide.",
  passwordEmpty: 'Saisissez votre mot de passe.',
  invalidCredentials: 'E-mail ou mot de passe incorrect. Réessayez.',
  emailNotVerified:
    'Votre adresse e-mail n’est pas encore vérifiée. Consultez votre boîte de réception.',
  genericError: 'Une erreur est survenue. Réessayez dans un instant.',
} as const;

function validate(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  if (email.trim() === '') {
    errors.email = COPY.emailEmpty;
  } else if (!EMAIL_RE.test(email.trim())) {
    errors.email = COPY.emailInvalid;
  }
  if (password === '') {
    errors.password = COPY.passwordEmpty;
  }
  return errors;
}

/**
 * Login card — recreates the BUILD-352 design (black & white, single white card)
 * and wires the form to the Tahiti auth service. Handles field validation,
 * form-level errors, the password show/hide toggle, a submitting state, and a
 * success state that triggers {@link LoginCardProps.onSuccess}.
 */
export function LoginCard({ onSuccess, initialMessage }: LoginCardProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(initialMessage ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const emailId = useId();
  const passwordId = useId();
  const emailErrorId = useId();
  const passwordErrorId = useId();

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  // Move focus to the form-level alert when it appears.
  useEffect(() => {
    if (formError !== null) {
      alertRef.current?.focus();
    }
  }, [formError]);

  // Surface a newly-arrived initialMessage even when the card is already mounted
  // — e.g. a 401 fires while the user is sitting on /login, so the route's
  // `reason=expired` (hence this prop) changes without a remount. The useState
  // initializer only reads the prop once, so without this the message wouldn't
  // show. Keyed on the prop so ordinary typing (which clears it) doesn't re-show.
  useEffect(() => {
    if (initialMessage !== undefined) setFormError(initialMessage);
  }, [initialMessage]);

  // Brief success animation, then hand off to the parent for redirection.
  useEffect(() => {
    if (!succeeded) return;
    const timer = window.setTimeout(onSuccess, 900);
    return () => window.clearTimeout(timer);
  }, [succeeded, onSuccess]);

  function revalidateField(field: keyof FieldErrors, nextEmail: string, nextPassword: string) {
    if (!submitted) return;
    const next = validate(nextEmail, nextPassword);
    setFieldErrors((prev) => ({ ...prev, [field]: next[field] }));
  }

  // A pre-filled message (e.g. session expired) is stale as soon as the user
  // starts a fresh attempt; only clear it once so a later submit failure can
  // still set (and keep) its own formError.
  function clearInitialMessage() {
    if (initialMessage !== undefined)
      setFormError((prev) => (prev === initialMessage ? null : prev));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
    setFormError(null);

    const errors = validate(email, password);
    setFieldErrors(errors);
    if (errors.email !== undefined) {
      emailRef.current?.focus();
      return;
    }
    if (errors.password !== undefined) {
      passwordRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await login(email.trim(), password);
      setSucceeded(true);
    } catch (error) {
      if (isEmailNotVerified(error)) {
        setFormError(COPY.emailNotVerified);
      } else if (error instanceof AuthError) {
        setFormError(COPY.invalidCredentials);
      } else {
        setFormError(COPY.genericError);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled = email.trim() === '' || password === '' || submitting;

  if (succeeded) {
    return (
      <div className="login-card login-card--success" aria-live="polite">
        <div className="login-success__badge" aria-hidden="true">
          <span className="material-symbols-rounded">check</span>
        </div>
        <h1 className="login-success__title">connexion réussie</h1>
        <p className="login-success__subtitle">redirection vers le dictionnaire…</p>
      </div>
    );
  }

  return (
    <div className="login-card">
      <img
        className="login-logo"
        src="/assets/logo-horizontal-transparent.png"
        alt="Patrick Mathieu Stratégie"
        width={252}
      />

      <h1 className="login-heading">se connecter</h1>
      <p className="login-subtitle">accédez au dictionnaire</p>

      {formError !== null && (
        <div className="login-alert" role="alert" tabIndex={-1} ref={alertRef}>
          <span className="material-symbols-rounded login-alert__icon" aria-hidden="true">
            error
          </span>
          <span>{formError}</span>
        </div>
      )}

      <form className="login-form" onSubmit={handleSubmit} noValidate>
        <div className="login-field">
          <label className="login-label" htmlFor={emailId}>
            adresse e-mail
          </label>
          <div className={`login-input-shell${fieldErrors.email ? ' is-error' : ''}`}>
            <input
              id={emailId}
              ref={emailRef}
              className="login-input"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="vous@exemple.fr"
              value={email}
              aria-invalid={fieldErrors.email ? true : undefined}
              aria-describedby={fieldErrors.email ? emailErrorId : undefined}
              onChange={(e) => {
                setEmail(e.target.value);
                revalidateField('email', e.target.value, password);
                clearInitialMessage();
              }}
            />
          </div>
          {fieldErrors.email && (
            <p className="login-field-error" id={emailErrorId}>
              <span className="material-symbols-rounded" aria-hidden="true">
                error
              </span>
              {fieldErrors.email}
            </p>
          )}
        </div>

        <div className="login-field">
          <label className="login-label" htmlFor={passwordId}>
            mot de passe
          </label>
          <div className={`login-input-shell${fieldErrors.password ? ' is-error' : ''}`}>
            <input
              id={passwordId}
              ref={passwordRef}
              className="login-input"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              aria-invalid={fieldErrors.password ? true : undefined}
              aria-describedby={fieldErrors.password ? passwordErrorId : undefined}
              onChange={(e) => {
                setPassword(e.target.value);
                revalidateField('password', email, e.target.value);
                clearInitialMessage();
              }}
            />
            <button
              type="button"
              className="login-password-toggle"
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((s) => !s)}
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          {fieldErrors.password && (
            <p className="login-field-error" id={passwordErrorId}>
              <span className="material-symbols-rounded" aria-hidden="true">
                error
              </span>
              {fieldErrors.password}
            </p>
          )}
        </div>

        <div className="login-row">
          <label className="login-remember">
            <input
              type="checkbox"
              className="login-checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span className="login-checkbox-box" aria-hidden="true">
              <span className="material-symbols-rounded">check</span>
            </span>
            se souvenir de moi
          </label>
          {/* Forgot-password is a non-functional stub for now. */}
          <button type="button" className="login-forgot" onClick={() => undefined}>
            mot de passe oublié ?
          </button>
        </div>

        <button
          type="submit"
          className="login-submit"
          disabled={submitDisabled}
          aria-busy={submitting}
        >
          {submitting ? (
            <>
              <span className="login-spinner" aria-hidden="true" />
              connexion…
            </>
          ) : (
            'se connecter'
          )}
        </button>
      </form>
    </div>
  );
}
